import { useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import moment from "moment-timezone";
import { useAppContextNonNull } from "@contexts/AppContext";
import OwButton from "@components/shared/OwButton/OwButton";
import OwBadge from "@components/shared/OwBadge/OwBadge";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import UnanetService from "@services/UnanetService";
import NotificationLevelEnum from "@models/NotificationLevelEnum";
import { IUnanetSyncLogDetailResponse, IUnanetQueueItem } from "@models/Unanet/IUnanetDashboardQueryParams";
import OwTranslate from "@components/shared/OwTranslate/OwTranslate";

const ACTION_LABELS: Record<string, string> = {
    journal_entry_create: "Journal Entry",
    journal_entry_add_expenses: "JE Expenses Added",
    journal_entry_get: "JE Retrieved",
    connection_test: "Connection Test",
    test_connection_adhoc: "Ad-hoc Test",
    project_search: "Project Search",
    accounts_get: "Accounts Retrieved",
    legal_entities_get: "Legal Entities",
    organizations_get: "Organizations",
};

interface Props {
    syncLogGuid: string;
    isOpen: boolean;
    onClose: (refresh?: boolean) => void;
}

export const UnanetQueueDetailModal = ({ syncLogGuid, isOpen, onClose }: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();

    const [detail, setDetail] = useState<IUnanetSyncLogDetailResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [retrying, setRetrying] = useState(false);
    const [resolving, setResolving] = useState(false);
    const [resolutionNote, setResolutionNote] = useState("");

    const fetchDetail = useCallback(async () => {
        if (!syncLogGuid) return;
        setLoading(true);
        try {
            const data = await UnanetService.getSyncLogDetail(syncLogGuid, userModel, supplierModel);
            setDetail(data);
        } catch (e) {
            console.error("Error fetching sync log detail:", e);
        } finally {
            setLoading(false);
        }
    }, [syncLogGuid, userModel, supplierModel]);

    useEffect(() => {
        if (isOpen && syncLogGuid) {
            fetchDetail();
            setResolutionNote("");
        }
    }, [isOpen, syncLogGuid, fetchDetail]);

    const handleRetry = async (queueGuid: string) => {
        setRetrying(true);
        try {
            await UnanetService.retryQueueItem(queueGuid, userModel, supplierModel);
            uiContext.showNotification({
                message: "retry_success",
                level: NotificationLevelEnum.success,
            });
            onClose(true);
        } catch (e) {
            console.error("Error retrying queue item:", e);
            uiContext.showNotification({
                message: "retry",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setRetrying(false);
        }
    };

    const handleResolve = async (queueGuid: string) => {
        setResolving(true);
        try {
            await UnanetService.resolveQueueItem(queueGuid, { resolution_note: resolutionNote || undefined }, userModel, supplierModel);
            uiContext.showNotification({
                message: "resolve_success",
                level: NotificationLevelEnum.success,
            });
            onClose(true);
        } catch (e) {
            console.error("Error resolving queue item:", e);
            uiContext.showNotification({
                message: "resolve",
                level: NotificationLevelEnum.error,
            });
        } finally {
            setResolving(false);
        }
    };

    const syncLog = detail?.syncLog;
    const queueEntries = detail?.queueEntries || [];

    const renderErrorMessages = (errorMessage: string | null) => {
        if (!errorMessage) return null;
        const parts = errorMessage
            .split("|")
            .map((s) => s.trim())
            .filter(Boolean);
        if (parts.length <= 1) return <span className="text-danger">{errorMessage}</span>;
        return (
            <ul className="UnanetQueueDetailModal__error-list">
                {parts.map((msg, i) => (
                    <li key={i}>{msg}</li>
                ))}
            </ul>
        );
    };

    const getStatusBgColor = (status: string) => {
        switch (status) {
            case "success":
                return "bg-success";
            case "failed":
                return "bg-danger";
            case "resolved":
                return "bg-secondary";
            default:
                return "bg-warning";
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            className="modalContentClass"
            overlayClassName="modalOverlayClass"
            style={{ overlay: { zIndex: 10 } }}
            ariaHideApp={false}>
            <div className="p-3">
                {/* Header */}
                <div className="UnanetQueueDetailModal__header">
                    <OwTitle title="details" />
                    <OwButton className="btn-danger btn-sm" centerIconClass="fas fa-times" fn={() => onClose()} />
                </div>

                {loading && (
                    <div className="text-center p-3">
                        <i className="fas fa-spinner fa-spin" /> Loading...
                    </div>
                )}

                {!loading && syncLog && (
                    <>
                        {/* Sync Log Info */}
                        <div className="UnanetQueueDetailModal__info-grid">
                            <span className="UnanetQueueDetailModal__info-label">
                                <OwLabel title="type" noBottomMargin />
                            </span>
                            <span className="badge bg-info">
                                <OwTranslate id={ACTION_LABELS[syncLog.action] || syncLog.action} />
                            </span>

                            <span className="UnanetQueueDetailModal__info-label">
                                <OwLabel title="status" noBottomMargin />
                            </span>
                            <span>
                                <OwBadge
                                    title={syncLog.status}
                                    bgColor={syncLog.status === "success" ? "bg-success" : "bg-danger"}
                                    leftIconClass={syncLog.status === "success" ? "fas fa-check" : "fas fa-times"}
                                />
                            </span>

                            {syncLog.orderGuid && syncLog.orderName && (
                                <>
                                    <span className="UnanetQueueDetailModal__info-label">
                                        <OwLabel title="order_reference" noBottomMargin />
                                    </span>
                                    <span>
                                        <a href={`/orders/${syncLog.orderGuid}`}>{syncLog.orderName}</a>
                                    </span>
                                </>
                            )}

                            <span className="UnanetQueueDetailModal__info-label">
                                <OwLabel title="timestamp" noBottomMargin />
                            </span>
                            <span>{moment(syncLog.createdAt).format("M/D/YY, h:mm:ss A")}</span>

                            {syncLog.errorMessage && (
                                <>
                                    <span className="UnanetQueueDetailModal__info-label">
                                        <OwLabel title="error" noBottomMargin skipTranslate />
                                    </span>
                                    <span>{renderErrorMessages(syncLog.errorMessage)}</span>
                                </>
                            )}
                        </div>

                        {/* Request/Response Payloads */}
                        {syncLog.requestPayload && (
                            <details className="mt-2">
                                <summary className="small text-muted" style={{ cursor: "pointer" }}>
                                    <OwLabel title="request_payload" noBottomMargin skipTranslate={false} />
                                </summary>
                                <div className="UnanetQueueDetailModal__payload-viewer">
                                    {JSON.stringify(syncLog.requestPayload, null, 2)}
                                </div>
                            </details>
                        )}
                        {syncLog.responsePayload && (
                            <details className="mt-2">
                                <summary className="small text-muted" style={{ cursor: "pointer" }}>
                                    <OwLabel title="response_payload" noBottomMargin skipTranslate={false} />
                                </summary>
                                <div className="UnanetQueueDetailModal__payload-viewer">
                                    {JSON.stringify(syncLog.responsePayload, null, 2)}
                                </div>
                            </details>
                        )}

                        {/* Queue Entries Section */}
                        {queueEntries.length > 0 && (
                            <div className="UnanetQueueDetailModal__sync-log-section mt-3">
                                <OwLabel title="queue_entries" isBold className="mb-2" />
                                {queueEntries.map((entry: IUnanetQueueItem) => {
                                    const canRetry = entry.status !== "pending" && entry.status !== "processing";
                                    const canResolve = entry.status === "failed";

                                    return (
                                        <div key={entry.guid} className="UnanetQueueDetailModal__sync-log-entry">
                                            <div className="UnanetQueueDetailModal__sync-log-header">
                                                <OwBadge title={entry.status} bgColor={getStatusBgColor(entry.status)} />
                                                <span className="small">
                                                    {entry.orderGuid ? (
                                                        <a href={`/orders/${entry.orderGuid}`}>{entry.orderName}</a>
                                                    ) : (
                                                        entry.orderName || "—"
                                                    )}
                                                </span>
                                                <span className="text-muted small ms-auto">
                                                    {moment(entry.createdAt).format("M/D/YY, h:mm:ss A")}
                                                </span>
                                            </div>
                                            {entry.errorMessage && (
                                                <div className="small mt-1">{renderErrorMessages(entry.errorMessage)}</div>
                                            )}
                                            {entry.processedAt && (
                                                <div className="text-muted small mt-1">
                                                    Processed: {moment(entry.processedAt).format("M/D/YY, h:mm:ss A")}
                                                </div>
                                            )}

                                            {/* Queue entry actions */}
                                            <div className="d-flex gap-2 mt-2">
                                                {canRetry && (
                                                    <OwButton
                                                        title="retry"
                                                        className="btn-warning btn-sm"
                                                        leftIconClass="fas fa-redo"
                                                        fn={() => handleRetry(entry.guid)}
                                                        active={retrying}
                                                        disableOnActive={true}
                                                    />
                                                )}
                                                {canResolve && (
                                                    <OwButton
                                                        title="mark_resolved"
                                                        className="btn-secondary btn-sm"
                                                        leftIconClass="fas fa-check"
                                                        fn={() => handleResolve(entry.guid)}
                                                        active={resolving}
                                                        disableOnActive={true}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Resolution note input for failed entries */}
                                {queueEntries.some((e) => e.status === "failed") && (
                                    <div className="UnanetQueueDetailModal__resolve-input mt-2">
                                        <OwLabel title="resolution_note" noBottomMargin />
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={resolutionNote}
                                            onChange={(e) => setResolutionNote(e.target.value)}
                                            placeholder="Optional note..."
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {!loading && !syncLog && (
                    <div className="text-center text-muted p-3">
                        <OwLabel title="no_data_available" noBottomMargin />
                    </div>
                )}
            </div>
        </Modal>
    );
};
